import { LocationQuery } from 'vue-router'
import GeneralResponse from '~/models/Api/GeneralResponse'
import ProductListResponse from '~/models/Api/ProductListResponse'
import ProductFilterListResponse from '~/models/Api/ProductFilterListResponse'
import { minimizeProductArray } from '~/utils/minimizeProductArray'
import Product from '~/models/Product/Product'
import Filter from '~/models/Filters/Filter'
import { useRoute } from '#imports'
import ProductListRequest from '~/models/Api/ProductListRequest'

interface ProductListAsyncDataRequest {
    needsFiltersAndRichContent: boolean
    inputPageFrom: number | null
    inputPageTo: number | null
    id: number | null
    type: string
    url?: string
}

function cleanRelativeUrl(relativeUrl: string) {
    const [pathAndQuery] = relativeUrl.split('#')
    const [path, queryString] = pathAndQuery.split('?')

    const params = new URLSearchParams(queryString || '')
    params.delete('page')

    const newQuery = params.toString()

    return path + (newQuery ? '?' + newQuery : '')
}

function constructKey(requestData: ProductListRequest) {
    const newRequestData = { ...requestData }
    newRequestData.currentUrl = cleanRelativeUrl(newRequestData.currentUrl || '')

    return `productListAsyncData_${JSON.stringify(newRequestData)}`
}

export function useProductListAsyncData({ inputPageFrom, inputPageTo, id, type, url }: ProductListAsyncDataRequest, lazy: boolean) {
    const requestData = getRequestDataWithRoute(type, id, inputPageFrom, inputPageTo, url)
    const key = constructKey(requestData)

    return useCachedAsyncData<GeneralResponse<ProductListResponse>>(
        `${key}_productList`,
        1800,
        async () => {
            const requestData = getRequestDataWithRoute(type, id, inputPageFrom, inputPageTo, url)

            const config = useRuntimeConfig()

            const productListResponse = await $fetch<GeneralResponse<ProductListResponse>>(`${config.public.apiUrl}/api/v3/product/list`, {
                method: 'POST',
                body: requestData,
                headers: {
                    platform: 'pwa',
                },
            })

            productListResponse.response.products.products = minimizeProductArray(productListResponse.response.products.products)

            for (const key in productListResponse.response.catalogListBanners) {
                productListResponse.response.products.products.splice(
                    parseInt(key),
                    0,
                    productListResponse.response.catalogListBanners[key] as unknown as Product
                )
            }

            productListResponse.response.content = decodeSeoUrl(productListResponse.response.content)

            return productListResponse
        },
        { lazy }
    )
}

export default async ({ needsFiltersAndRichContent, inputPageFrom, inputPageTo, id, type, url }: ProductListAsyncDataRequest) => {
    const route = useRoute()
    const config = useRuntimeConfig()
    const requestData = getRequestDataWithRoute(type, id, inputPageFrom, inputPageTo, url)

    if (requestData.type === 'search' && (!route.query.q || route.query.q === '')) {
        await safeNavigateTo('/')
    }

    const key = constructKey(requestData)

    const [
        { data: productListData, refresh: productListRefresh },
        { data: filtersData, refresh: filtersRefresh },
        { data: richContentData, refresh: richContentRefresh },
    ] = await Promise.all([
        useProductListAsyncData({ inputPageFrom, inputPageTo, id, type, url, needsFiltersAndRichContent: false }, false),
        useCachedAsyncData(
            `${key}_filters`,
            1800,
            async () => {
                if (!needsFiltersAndRichContent) {
                    return null
                }

                const filtersResponse = await $fetch<GeneralResponse<ProductFilterListResponse>>(
                    `${config.public.apiUrl}/api/v2/product/filter/list`,
                    {
                        method: 'POST',
                        body: requestData,
                        headers: {
                            platform: 'pwa',
                        },
                    }
                )

                if (!filtersResponse.result) {
                    return null
                }

                if (filtersResponse?.response?.filters) {
                    filtersResponse.response.quickFilters = []
                    filtersResponse.response.filters = filtersResponse.response.filters.map((filter: Filter) => {
                        return {
                            ...filter,
                            urlMask: decodeSeoUrl(filter.urlMask || ''),
                            options: (filter.options || []).map((option) => {
                                return {
                                    ...option,
                                    url: decodeSeoUrl(option.url || ''),
                                }
                            }),
                            items: (filter.items || []).map((item) => {
                                return {
                                    ...item,
                                    url: decodeSeoUrl(item.url || ''),
                                }
                            }),
                        }
                    })
                }

                return filtersResponse
            },
            { lazy: false }
        ),
        useCachedAsyncData(
            `${key}_richContent`,
            1800,
            async () => {
                if (!needsFiltersAndRichContent) {
                    return null
                }

                return await $fetch(`${config.public.apiUrl}/api/v2/product/list/rich-content`, {
                    method: 'POST',
                    body: requestData,
                    headers: {
                        platform: 'pwa',
                    },
                })
            },
            { lazy: false }
        ),
    ])

    return { productListData, productListRefresh, filtersData, filtersRefresh, richContentData, richContentRefresh }
}

function resolveVisualTagIds() {
    const route = useRoute()

    const visualTagIds = []
    for (const [key, value] of Object.entries(route.query)) {
        if (key.startsWith('vt[')) {
            visualTagIds.push(parseInt(value as string))
        }
    }

    return visualTagIds
}

function resolveNutraRatings() {
    const route = useRoute()

    const nutraRatings = []
    for (const [key, value] of Object.entries(route.query)) {
        if (key.startsWith('nutraRating[')) {
            nutraRatings.push(value)
        }
    }

    return nutraRatings
}

function getRequestDataWithRoute(
    type: string,
    id: number | null,
    inputPageFrom: number | null,
    inputPageTo: number | null,
    url: string | undefined
): ProductListRequest {
    const route = useRoute()
    const routeParams = route.matched[0].props.default as { [name: string]: string }

    return getRequestData(type, id, inputPageFrom, inputPageTo, url, route.fullPath, route.query, routeParams)
}

function getRequestData(
    type: string,
    id: number | null,
    inputPageFrom: number | null,
    inputPageTo: number | null,
    url: string | undefined,
    fullPath: string,
    queryParams: LocationQuery,
    routeParams: { [name: string]: string }
): ProductListRequest {
    const value: any = {}
    Object.entries(queryParams).forEach(([key, val]) => {
        const match = key.match(/\[(\d+)\]/)
        if (match) {
            const keyNumber = parseInt(match[1], 10)
            const propMatch = key.match(/\[\d+\]\[(\w+)\]/)

            if (propMatch) {
                value[keyNumber] = value[keyNumber] || {}
                value[keyNumber][propMatch[1]] = val
            }
        }
    })
    const requestFilters: { identifier: string; value: any }[] = []
    requestFilters.push({
        identifier: 'property',
        value,
    })

    for (const [identifier, value] of Object.entries(routeParams)) {
        if (['componentName', 'id'].includes(identifier)) {
            continue
        }

        requestFilters.push({
            identifier,
            value,
        })
    }

    const serviceOfficeCities = queryParams.soc || []
    if (serviceOfficeCities.length) {
        requestFilters.push({
            identifier: 'soc',
            value: serviceOfficeCities,
        })
    }

    const minRating = queryParams.minRating || null
    if (minRating) {
        requestFilters.push({
            identifier: 'minRating',
            value: minRating,
        })
    }

    const nutraRatings = resolveNutraRatings()
    if (nutraRatings.length) {
        requestFilters.push({
            identifier: 'nutraRating',
            value: nutraRatings,
        })
    }

    const visualTagIds = resolveVisualTagIds()
    if (visualTagIds.length) {
        requestFilters.push({
            identifier: 'vt',
            value: visualTagIds,
        })
    }

    const luigisBoxUserIdCookie = useCookie('_lb')
    const luigisBoxUserId = luigisBoxUserIdCookie.value || null

    const pageFrom = parseInt((inputPageFrom || queryParams.page || 1) as string)
    const pageTo = parseInt((inputPageTo || queryParams.pageTo || pageFrom) as string)

    return {
        type,
        currentUrl: url || fullPath,
        sortMethod: parseInt((routeParams.s || '1') as string),
        pageFrom,
        pageTo,
        filters: requestFilters,
        priceFrom: (queryParams.priceFrom || null) as string | null,
        priceTo: (queryParams.priceTo || null) as string | null,
        servicesOnly: false,
        id,
        query: queryParams.q as string | null,
        addAdditionalSubmenuTiles: true,
        luigisBoxUserId,
        skipFilters: true,
        skipRichContentWidgets: true,
        superPriceOnly: !!parseInt(queryParams.superPriceOnly as string),
        uid: useUidCookie().value,
    }
}
